import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Phone, Mail, MapPin, User, Briefcase, GraduationCap, Award, Globe, Users, Search } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const GoldenCurveTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#F39C12';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#2C3E50', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+01234 567 890' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'yourmailishere' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'yoururl.com is here' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Street Adresse' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat. Ut wisi enim ad minim veniam, quis nostrud exerci tation ullamcorper suscipit lobortis nisl ut aliquip ex ea commodo consequat.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974&auto=format&fit=crop'
        : '');

    const hasSkills = (data.skills && data.skills.length > 0) || showPlaceholders;
    const hasInterests = (data.interests && data.interests.length > 0) || showPlaceholders;
    const hasExperience = data.experience.length > 0 || showPlaceholders;
    const hasEducation = data.education.length > 0 || showPlaceholders;
    const hasLanguages = (data.languages && data.languages.length > 0) || showPlaceholders;
    const hasReferences = (data.references && data.references.length > 0) || showPlaceholders;

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex relative overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>

            {/* Decorative curves (slightly reduced) */}
            <div className="absolute -top-16 -left-28 w-[420px] h-[420px] rounded-full z-0" style={{ backgroundColor: primaryColor }}></div>
            <div className="absolute top-0 left-0 w-[380px] h-[480px] overflow-hidden z-[1]">
                <svg viewBox="0 0 400 600" preserveAspectRatio="none" className="w-full h-full">
                    <path d="M0,0 L400,0 Q300,300 400,600 L0,600 Z" fill="white" />
                </svg>
            </div>

            {/* Left Sidebar */}
            <div data-cv-sidebar className="w-[38%] text-white p-8 pt-28 relative z-10 overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                        {(photoSrc || showPlaceholders) && (
                            <div className="absolute -top-2 left-10 w-40 h-40 rounded-full border-8 border-white overflow-hidden shadow-2xl bg-slate-300" style={{ outline: `6px solid ${imageBorderColor}`, outlineOffset: '-14px' }}>
                                {photoSrc ? (
                                    <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                                ) : null}
                            </div>
                        )}

                        <div className="mt-36 mb-4">
                            <h1 className="cv-fs-name text-4xl font-black uppercase leading-none mb-2 text-white">
                                {data.personalInfo.firstName || (showPlaceholders ? 'KAREN' : '')}<br />
                                {data.personalInfo.lastName || (showPlaceholders ? 'RICHARD' : '')}
                            </h1>
                            <p className="cv-fs-profession text-base font-bold uppercase tracking-wider" style={{ color: primaryColor }}>
                                {data.personalInfo.profession || (showPlaceholders ? 'PROFESSIONAL TITLE' : '')}
                            </p>
                        </div>

                        {(phone || email || website || address || showPlaceholders) && (
                            <section className="mb-4">
                                <div className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full mb-4" style={{ backgroundColor: primaryColor }}>
                                    <Phone className="w-4 h-4" />
                                    <h2 className="text-sm font-black uppercase">{CV_LABELS.contact}</h2>
                                </div>
                                <div className="cv-fs-contact space-y-3 pl-2">
                                    {phone && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                                                <Phone className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs">{phone}</span>
                                        </div>
                                    )}
                                    {email && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                                                <Mail className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs break-all">{email}</span>
                                        </div>
                                    )}
                                    {website && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                                                <Globe className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs">{website}</span>
                                        </div>
                                    )}
                                    {address && (
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: primaryColor }}>
                                                <MapPin className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs">{address}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {hasSkills && (
                            <section className="mb-4">
                                <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="pill" />
                                <div className="cv-fs-skills grid grid-cols-3 gap-2 pl-2">
                                    {(data.skills && data.skills.length > 0 ? data.skills : [
                                        { name: 'SEO', level: 95 },
                                        { name: 'Graphisme', level: 90 },
                                        { name: 'Web', level: 80 }
                                    ]).map((skill, i) => (
                                        <div
                                            key={skill.id || skill.name || i}
                                            className="flex flex-col items-center min-w-0"
                                            data-cv-block
                                            data-cv-item-id={skill.id || undefined}
                                        >
                                            <div className="relative w-12 h-12 mb-1">
                                                <svg className="transform -rotate-90 w-12 h-12">
                                                    <circle cx="24" cy="24" r="20" stroke="#1A252F" strokeWidth="4" fill="transparent" />
                                                    <circle
                                                        cx="24"
                                                        cy="24"
                                                        r="20"
                                                        stroke={primaryColor}
                                                        strokeWidth="4"
                                                        fill="transparent"
                                                        strokeDasharray={2 * Math.PI * 20}
                                                        strokeDashoffset={2 * Math.PI * 20 * (1 - (skill.level || 50) / 100)}
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-[9px] font-black" style={{ color: primaryColor }}>{skill.level}%</span>
                                                </div>
                                            </div>
                                            <span className="text-[8px] font-bold text-center leading-tight break-words w-full">{skill.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {hasInterests && (
                            <section className="mb-4">
                                <div className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full mb-4" style={{ backgroundColor: primaryColor }}>
                                    <Search className="w-4 h-4" />
                                    <h2 className="text-sm font-black uppercase">{CV_LABELS.interests}</h2>
                                </div>
                                <div className="cv-fs-interests grid grid-cols-3 gap-2 pl-2">
                                    {(data.interests && data.interests.length > 0 ? data.interests : [
                                        { name: 'Natation' }, { name: 'Vélo' },
                                        { name: 'Voyage' }, { name: 'Échecs' },
                                        { name: 'Musique' }, { name: 'Cinéma' },
                                        { name: 'Lecture' }, { name: 'Vidéos' }
                                    ]).map((interest, i) => (
                                        <div
                                            key={interest.id || interest.name || i}
                                            className="flex items-center gap-2"
                                            data-cv-block
                                            data-cv-item-id={interest.id || undefined}
                                        >
                                            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: primaryColor }}></div>
                                            <span className="text-xs font-medium">{interest.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>

            {/* Right Content */}
            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-10 pt-12 relative z-10" style={{ backgroundColor: backgroundColor.toLowerCase() === '#ffffff' ? '#f8fafc' : backgroundColor }}>

                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-4">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-3">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {summary && (
                    <section className="mb-4">
                        <div className="inline-flex items-center gap-2 text-white px-5 py-1.5 rounded-full mb-3 shadow-lg" style={{ backgroundColor: primaryColor }}>
                            <User className="w-4 h-4" />
                            <h2 className="text-sm font-black uppercase">{CV_LABELS.about}</h2>
                        </div>
                        <p className="cv-fs-summary text-xs leading-relaxed" style={{ color: '#' === textColor[0] ? textColor : '#334155' }}>
                            {summary}
                        </p>
                    </section>
                )}

                {hasExperience && (
                    <section className="mb-4">
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="pill" />
                        <div className="cv-fs-experience space-y-4">
                            {data.experience.length > 0 ? data.experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    className="flex gap-4"
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: primaryColor }}></div>
                                        {i < data.experience.length - 1 && <div className="w-0.5 flex-1 bg-slate-300 mt-2"></div>}
                                    </div>
                                    <div className="flex-1 pb-3">
                                        <p className="text-sm font-black mb-1" style={{ color: textColor }}>
                                            {exp.startDate}-{frPresent(exp.isCurrent, exp.endDate)}
                                        </p>
                                        <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>{exp.position}</h3>
                                        <p className="text-xs font-bold mb-2" style={{ color: textColor, opacity: 0.8 }}>{exp.company}</p>
                                        <p className="text-xs leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>{exp.description}</p>
                                    </div>
                                </div>
                            )) : (
                                <>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 mb-1">2018-2020</p>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Poste</h3>
                                            <p className="text-xs font-bold text-slate-600 mb-2">Entreprise</p>
                                            <p className="text-xs text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diam nonummy nibb euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 mb-1">2017-2018</p>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Poste</h3>
                                            <p className="text-xs font-bold text-slate-600 mb-2">Établissement</p>
                                            <p className="text-xs text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diam nonummy nibb euismod tincidunt ut laoreet dolore magna aliquam erat volutpat.
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}

                {hasEducation && (
                    <section className="mb-4">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="pill" />
                        <div className="cv-fs-education space-y-4">
                            {data.education.length > 0 ? data.education.map((edu, i) => (
                                <div
                                    key={edu.id || i}
                                    className="flex gap-4"
                                    data-cv-block
                                    data-cv-item-id={edu.id || undefined}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: primaryColor }}></div>
                                        {i < data.education.length - 1 && <div className="w-0.5 flex-1 bg-slate-300 mt-2"></div>}
                                    </div>
                                    <div className="flex-1 pb-3">
                                        <p className="text-sm font-black text-slate-900 mb-1">
                                            {edu.startDate.slice(0, 4)}-{edu.isCurrent ? CV_LABELS.present : edu.endDate.slice(0, 4)}
                                        </p>
                                        <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>{edu.degree}</h3>
                                        <p className="text-xs font-bold text-slate-600 mb-2">{edu.institution}</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">
                                            {edu.field}. {edu.description}
                                        </p>
                                    </div>
                                </div>
                            )) : (
                                <>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 mb-1">2011-2014</p>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Diplôme</h3>
                                            <p className="text-xs font-bold text-slate-600 mb-2">Établissement</p>
                                            <p className="text-xs text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diam nonummy nibb euismod tincidunt.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <p className="text-sm font-black text-slate-900 mb-1">2007-2010</p>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Diplôme</h3>
                                            <p className="text-xs font-bold text-slate-600 mb-2">Établissement</p>
                                            <p className="text-xs text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diam nonummy nibb euismod tincidunt.
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}

                {hasLanguages && (
                    <section className="mb-4">
                        <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor={primaryColor} variant="pill" />
                        <div className="cv-fs-languages space-y-3">
                            {(data.languages && data.languages.length > 0 ? data.languages.slice(0, 4) : [
                                { name: 'English', level: 100 },
                                { name: 'French', level: 90 },
                                { name: 'Spanish', level: 85 },
                                { name: 'German', level: 80 }
                            ]).map((lang, i) => (
                                <div
                                    key={lang.id || lang.name || i}
                                    data-cv-block
                                    data-cv-item-id={lang.id || undefined}
                                >
                                    <div className="flex justify-between mb-1">
                                        <span className="text-sm font-bold text-slate-900">{lang.name}</span>
                                    </div>
                                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${lang.level}%`, backgroundColor: primaryColor }}
                                        ></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {hasReferences && (
                    <section className="mb-4">
                        <div className="inline-flex items-center gap-2 text-white px-5 py-1.5 rounded-full mb-3 shadow-lg" style={{ backgroundColor: primaryColor }}>
                            <Users className="w-4 h-4" />
                            <h2 className="text-sm font-black uppercase">{CV_LABELS.references}</h2>
                        </div>
                        <div className="cv-fs-references space-y-4">
                            {(data.references && data.references.length > 0 ? data.references.slice(0, 2) : [
                                { name: 'Mark Johnson', position: 'Director', company: 'Entreprise', phone: '+000 1234 6789', email: 'mail@here' },
                                { name: 'Alyssa John', position: 'Chairman', company: 'Entreprise', phone: '+000 1234 6789', email: 'mail@here' }
                            ]).map((ref, i) => (
                                <div
                                    key={ref.id || i}
                                    className="border-l-4 pl-4"
                                    style={{ borderColor: primaryColor }}
                                    data-cv-block
                                    data-cv-item-id={ref.id || undefined}
                                >
                                    <p className="text-sm font-black text-slate-900">{ref.name}</p>
                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>{ref.position} of {ref.company}</p>
                                    <p className="text-xs text-slate-600">Phone: {ref.phone}</p>
                                    <p className="text-xs text-slate-600">Email: {ref.email}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default GoldenCurveTemplate;
